import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';

import { AppearanceService } from '../../services/appearance.service';
import { MaterialService } from '../../services/material.service';
import { NavigationService } from '../../services/navigation.service';
import { CheckboxComponent, DropdownComponent, SliderComponent } from '../../ui';

@Component({
  selector: 'app-settings-panel',
  imports: [CommonModule, SliderComponent, DropdownComponent, CheckboxComponent, TranslocoPipe],
  templateUrl: './settings-panel.component.html',
  styleUrls: ['./settings-panel.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsPanelComponent {
  protected appearance = inject(AppearanceService);
  protected material = inject(MaterialService);
  protected navigation = inject(NavigationService);

  // Signals from AppearanceService – bind directly in template via appearance.fov() etc.
  get fieldOfView(): number {
    return this.appearance.fov();
  }
  get edlEnabled(): boolean {
    return this.appearance.edlEnabled();
  }
  get edlRadius(): number {
    return this.appearance.edlRadius();
  }
  get edlStrength(): number {
    return this.appearance.edlStrength();
  }
  get edlOpacity(): number {
    return this.appearance.edlOpacity();
  }
  get currentQuality(): string {
    return this.appearance.quality();
  }
  get qualityOptions() {
    return this.appearance.getQualityOptions();
  }
  get pointBudget(): number {
    return this.appearance.pointBudget();
  }
  get minNodeSize(): number {
    return this.appearance.minNodeSize();
  }
  pointSize = signal(0.6);

  get currentPointShape(): string {
    return this.material.pointShape();
  }
  get pointShapeOptions() {
    return this.material.getPointShapeOptions();
  }
  get currentPointSizeType(): string {
    return this.material.pointSizeType();
  }
  get pointSizeTypeOptions() {
    return this.material.getPointSizeTypeOptions();
  }
  get currentCameraMode(): string {
    return this.navigation.cameraMode();
  }
  get cameraModeOptions() {
    return this.navigation.getCameraModeOptions();
  }
  formatNumber(value: number): string {
    if (value >= 1000000) {
      return (value / 1000000).toFixed(1) + 'M';
    } else if (value >= 1000) {
      return (value / 1000).toFixed(0) + 'K';
    }
    return value.toString();
  }

  onFOVChange(v: number | [number, number]): void {
    if (typeof v === 'number') {
      this.appearance.fov.set(v);
    }
  }

  onEDLEnabledChange(e: boolean): void {
    this.appearance.edlEnabled.set(e);
  }

  onEDLRadiusChange(v: number | [number, number]): void {
    if (typeof v === 'number') {
      this.appearance.edlRadius.set(v);
    }
  }

  onEDLStrengthChange(v: number | [number, number]): void {
    if (typeof v === 'number') {
      this.appearance.edlStrength.set(v);
    }
  }

  onEDLOpacityChange(v: number | [number, number]): void {
    if (typeof v === 'number') {
      this.appearance.edlOpacity.set(v);
    }
  }

  onQualityChange(q: string): void {
    this.appearance.quality.set(q);
  }

  onPointBudgetChange(v: number | [number, number]): void {
    if (typeof v === 'number') {
      this.appearance.pointBudget.set(v);
    }
  }

  onMinNodeSizeChange(v: number | [number, number]): void {
    if (typeof v === 'number') {
      this.appearance.minNodeSize.set(v);
    }
  }

  onPointSizeChange(v: number | [number, number]): void {
    if (typeof v === 'number') {
      this.material.pointSize.set(v);
    }
  }

  onPointShapeChange(s: string): void {
    this.material.pointShape.set(s);
  }

  onPointSizeTypeChange(t: string): void {
    this.material.pointSizeType.set(t);
  }

  onCameraModeChange(m: string): void {
    this.navigation.cameraMode.set(m);
  }
}
